import * as React from 'react'
import styled from 'styled-components'
import I18n from 'core/i18n'
import * as constants from 'static/constants'
import { AvatarImage } from 'components/atoms'
import { otaReservationService } from 'core/services'
import Memo from 'components/organisms/Tours/Weekly/Drawer/Memo'
import SvgIcon from 'components/atoms/SvgIcon'
import { IWeeklyOtaReservation } from 'utils/weeklyTypes'
import { OtaCheckinStatus } from 'static/interfaces'
import { WeeklyInfoCard } from 'components/atoms/weekly/WeeklyInfoCard'

// ドロップダウンの選択肢は「その状態にする」操作として表記する
const CHECKIN_STATUS_LABELS: Record<OtaCheckinStatus, string> = {
  not_yet: '未チェックイン',
  checked_in: 'チェックイン',
  no_show: 'No Show',
}

interface IProps {
  otaReservation: IWeeklyOtaReservation
  handleAfterUpdate: (target: any, params: any) => void
  setGroupSelectingResource: React.Dispatch<React.SetStateAction<string | null>>
  groupSelectingResource: string | null
  groupSelectedId: number
  guide: any
  setJustAssignedOtaReservations: (ota_reservations: IWeeklyOtaReservation[]) => void
  justAssignedOtaReservations: IWeeklyOtaReservation[]
}

const Guest: React.VFC<IProps> = React.memo(
  ({
    otaReservation,
    handleAfterUpdate,
    setGroupSelectingResource,
    groupSelectingResource,
    groupSelectedId,
    guide,
    setJustAssignedOtaReservations,
    justAssignedOtaReservations,
  }) => {
    const [checkinStatus, setCheckinStatus] = React.useState(otaReservation.checkin_status)
    const [checkinMenuOpen, setCheckinMenuOpen] = React.useState(false)
    const checkinRef = React.useRef<HTMLDivElement>(null)
    const [openDetails, setOpenDetails] = React.useState(false)
    const { connection } = otaReservation

    const handleCheckIn = React.useCallback(
      async (newStatus: OtaCheckinStatus) => {
        setCheckinMenuOpen(false)
        try {
          await otaReservationService.checkIn(otaReservation.id, newStatus)
          setCheckinStatus(newStatus)
          handleAfterUpdate(otaReservation, { checkin_status: newStatus })
        } catch (e) {
          console.error(e)
        }
      },
      [otaReservation, handleAfterUpdate]
    )

    React.useEffect(() => {
      if (!checkinMenuOpen) {
        return
      }
      const handleClickOutside = (event: MouseEvent) => {
        if (checkinRef.current && !checkinRef.current.contains(event.target as Node)) {
          setCheckinMenuOpen(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [checkinMenuOpen])

    const handleUpdateTourGroup = React.useCallback(
      async tourGroupId => {
        const updateTourGroup = async () => {
          const { updatedOtaReservation } = await otaReservationService.update(otaReservation.id, {
            jwt_tour_group_id: tourGroupId,
          })
          const params = {
            jwt_tour_group_id: updatedOtaReservation.jwt_tour_group_id,
            connection: updatedOtaReservation.connection,
          }
          if (tourGroupId) {
            setJustAssignedOtaReservations(
              justAssignedOtaReservations.concat([{ ...otaReservation, ...params }])
            )
          } else {
            setJustAssignedOtaReservations(
              justAssignedOtaReservations.filter(jr => jr.id !== otaReservation.id)
            )
          }
          handleAfterUpdate(otaReservation, params)
        }
        const isConnected = connection?.status === 'connected'
        try {
          if (tourGroupId !== null || !isConnected) {
            await updateTourGroup()
            return
          }

          if (confirm('本当に解除しますか？（解除メールが送信されます）')) {
            await updateTourGroup()
          }
        } catch (e) {
          console.error(e)
        }
      },
      [otaReservation, connection?.status, handleAfterUpdate, justAssignedOtaReservations]
    )

    React.useEffect(() => {
      if (groupSelectedId && groupSelectingResource === `ota_reservation_${otaReservation.id}`) {
        handleUpdateTourGroup(groupSelectedId)
        setGroupSelectingResource(null)
      }
    }, [groupSelectedId])

    const isActive = React.useMemo(() => {
      return ['confirmed', 'pending'].includes(otaReservation.booking_status_id)
    }, [otaReservation])

    const isJustAssigned = React.useMemo(() => {
      return justAssignedOtaReservations.find(jr => jr.id === otaReservation.id)
    }, [otaReservation, justAssignedOtaReservations])

    const wrapperClassName = React.useMemo(() => {
      const connected = connection?.status ?? ''
      const selected =
        groupSelectingResource === `ota_reservation_${otaReservation.id}` ? 'selected' : ''
      return `${connected} ${selected}`
    }, [connection?.status, groupSelectingResource])

    return (
      <Wrapper className={wrapperClassName}>
        <div className="section headSection">
          {isJustAssigned && (
            <div className="justAssigned">
              <SvgIcon
                name="check_circle_filled"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
              ツアーグループに追加されました
              <div className="cancelAssign" onClick={() => handleUpdateTourGroup(null)}>
                取り消し
              </div>
            </div>
          )}
          <div className="guestRow">
            <a
              href={`/agent/ota_reservations/${otaReservation.id}`}
              target="_blank"
              rel="noopener"
              className="guestName"
              aria-label="to guest info"
            >
              {connection?.status === 'connected' && (
                <SvgIcon
                  name="link_circle"
                  className="connection-icon"
                  ariaHidden={true}
                  role="presentation"
                />
              )}
              <div className="customerName">
                {otaReservation.custom_guest_name || otaReservation.customer_full_name}
              </div>
              <SvgIcon
                name="chevron_right"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
            </a>
            {guide && (
              <a
                href={`/agent/guides/${guide.id}/edit`}
                target="_blank"
                rel="noopener"
                className="guideName"
                aria-label="to guide info"
              >
                <AvatarImage size={24} avatarURL={guide.avatar_url} />
                <div className="guideNameInner">{`${guide.username}`}</div>
              </a>
            )}
          </div>
          <div className="createdAt">
            <p>
              <span>作成日時</span>
              {otaReservation.created_at}
            </p>
          </div>
          <div className="guestRow">
            <div className="guestNum">
              <span>
                <b>{otaReservation.num_of_adults}</b> x Adult
              </span>
              <span>
                <b>{otaReservation.num_of_children}</b> x Child
              </span>
              <span>
                <b>{otaReservation.num_of_infants}</b> x Infant
              </span>
            </div>
            {isActive && !isJustAssigned && (
              <div
                ref={checkinRef}
                className={`checkInToggle ${{ checked_in: 'checkedIn', no_show: 'noShow' }[
                  checkinStatus
                ] || ''}`}
              >
                {checkinStatus === 'no_show' ? (
                  <span className="body">No Show</span>
                ) : checkinStatus === 'checked_in' ? (
                  <span
                    onClick={() => handleCheckIn('not_yet')}
                    className="body"
                    role="button"
                    aria-label="toggle checkin"
                  >
                    <SvgIcon name="check" className="icon" ariaHidden={true} role="presentation" />
                    チェックイン済
                  </span>
                ) : (
                  <span
                    onClick={() => handleCheckIn('checked_in')}
                    className="body"
                    role="button"
                    aria-label="toggle checkin"
                  >
                    <SvgIcon
                      name="radio_button_unchecked"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    チェックイン
                  </span>
                )}
                <span
                  onClick={() => setCheckinMenuOpen(!checkinMenuOpen)}
                  className="arrow"
                  role="button"
                  aria-label="open checkin status menu"
                >
                  <SvgIcon
                    name="expand_more"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                </span>
                {checkinMenuOpen && (
                  <ul className="menu">
                    {(Object.keys(CHECKIN_STATUS_LABELS) as OtaCheckinStatus[])
                      .filter(status => status !== checkinStatus)
                      .map(status => (
                        <li key={status} onClick={() => handleCheckIn(status)} role="button">
                          {CHECKIN_STATUS_LABELS[status]}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
        {otaReservation.other_requests && otaReservation.other_requests.trim() && (
          <div className="section">
            <details className="otherRequestsContainer">
              <summary className="otherRequestsLabel">Other Requests</summary>
              <div className="otherRequestsContent">{otaReservation.other_requests}</div>
            </details>
          </div>
        )}
        {otaReservation.pickup_location && (
          <div className="section">
            <WeeklyInfoCard classNames="pickupLocation">
              {otaReservation.pickup_location}
            </WeeklyInfoCard>
          </div>
        )}
        <div className="section actionSection">
          <Memo
            target={otaReservation}
            targetClass="otaReservation"
            internal={true}
            external={true}
            handleAfterUpdate={handleAfterUpdate}
          />
          {otaReservation.jwt_tour_group_id && !isJustAssigned ? (
            <div
              onClick={() => handleUpdateTourGroup(null)}
              className="tgToggle tgRemove"
              role="button"
              aria-label="Unassign from tour group"
            >
              <SvgIcon
                name="remove_circle"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
              ツアーグループ解除
            </div>
          ) : (isActive && groupSelectingResource === `ota_reservation_${otaReservation.id}`) ||
            isJustAssigned ? (
            <div
              onClick={() =>
                isJustAssigned ? handleUpdateTourGroup(null) : setGroupSelectingResource(null)
              }
              role="button"
              className="tgToggle tgCancel"
              aria-label="Cancel"
            >
              <SvgIcon name="close_circle" className="icon" ariaHidden={true} role="presentation" />
              キャンセル
            </div>
          ) : isActive ? (
            <div
              onClick={() => setGroupSelectingResource(`ota_reservation_${otaReservation.id}`)}
              className="tgToggle tgAdd"
              role="button"
              aria-label="Assign to tour group"
            >
              <SvgIcon name="add_circle" className="icon" ariaHidden={true} role="presentation" />
              ツアーグループに追加
            </div>
          ) : (
            <div className="tgCanceled">{otaReservation.booking_status_id}</div>
          )}
        </div>
        {openDetails && (
          <div className="section details">
            <p>
              <span>予約ID</span>
              {otaReservation.uuid}
            </p>
            <p>
              <span>Eメール</span>
              {otaReservation.customer_email}
            </p>
            <p>
              <span>電話番号</span>
              {otaReservation.customer_phone}
            </p>
            <p>
              <span>料金</span>￥{otaReservation.total_price.toLocaleString()}
            </p>
            <p>
              <span>ステータス</span>
              {I18n.t(
                `enums.ota_reservation.booking_status_id.${otaReservation.booking_status_id}`,
                { locale: 'ja' }
              )}
            </p>
          </div>
        )}
        <div
          onClick={e => {
            e.stopPropagation()
            setOpenDetails(!openDetails)
          }}
          className="expandBtn"
          role="button"
          aria-label="Toggle guest detailed info"
        >
          {openDetails ? (
            <SvgIcon name="expand_less" className="icon" ariaHidden={true} role="presentation" />
          ) : (
            <SvgIcon name="expand_more" className="icon" ariaHidden={true} role="presentation" />
          )}
        </div>
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  position: relative;
  margin-bottom: 12px;
  background-color: ${constants.COLORS.white};
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 12px;
  transition: border-color 0.2s;
  &.selected,
  &.connected {
    border-color: ${constants.COLORS.primary};
  }
  &.selected {
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: -10px;
      transform: translateY(-50%);
      animation: slideinRight2 0.2s;
      border-top: 8px solid transparent;
      border-right: 10px solid ${constants.COLORS.primary};
      border-bottom: 8px solid transparent;
    }
  }
  &:hover {
    .expandBtn {
      opacity: 1;
      &:hover {
        transform: scale(1.1);
      }
    }
  }
  // カード内を区切り線でセクション分けする
  .section {
    display: flow-root;
    padding: 12px 16px;
    & + .section {
      border-top: 1px solid ${constants.COLORS.divider2};
    }
  }
  .justAssigned {
    display: flex;
    align-items: center;
    gap: 4px;

    color: ${constants.COLORS.white};
    font-size: 12px;
    background-color: ${constants.COLORS.primary};
    border-radius: 6px;
    padding: 3px 5px;
    margin-bottom: 4px;

    .icon {
      width: 16px;
      height: 16px;
      margin-right: 2px;
      opacity: 1;
      transition: opacity 0.2s;
    }

    .cancelAssign {
      margin-left: 8px;
      font-weight: bold;
      font-size: 11px;
      text-decoration: underline;
      margin-right: 18px;
      cursor: pointer;
    }
  }
  .guestName,
  .guideName {
    display: flex;
    align-items: center;
    line-height: 24px;
    margin-bottom: 4px;
    &:hover {
      color: ${constants.COLORS.primary};
    }
  }
  .guestName {
    color: ${constants.COLORS.black};
    font-size: 14px;
    transition: color 0.2s;
    margin-right: 4px;
    .icon {
      flex: 0 0 auto;
      width: 20px;
      height: 20px;
    }
  }
  .guideName {
    color: ${constants.COLORS.subText};
    font-size: 12px;
    > div {
      margin-right: 8px;
    }
  }
  .customerName,
  .guideNameInner {
    line-height: 16px;
    word-break: break-all;
  }
  .guestRow {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .connection-icon {
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }
  }

  .guestNum {
    span {
      color: ${constants.COLORS.subText};
      font-size: 12px;
      margin-right: 12px;
      b {
        color: ${constants.COLORS.black};
        font-weight: bold;
      }
    }
  }
  .checkInToggle {
    position: relative;
    display: flex;
    line-height: 16px;
    color: ${constants.COLORS.subText};
    font-size: 11px;
    letter-spacing: 0;
    background-color: ${constants.COLORS.background2};
    border-radius: 4px;
    .body,
    .arrow {
      display: flex;
      align-items: center;
      cursor: pointer;
      transition: background-color 0.2s;
      &:hover {
        background-color: ${constants.COLORS.successBG2};
      }
      .icon {
        width: 16px;
        height: 16px;
      }
    }
    .body {
      padding: 4px 8px 4px 4px;
      border-radius: 4px 0 0 4px;
      .icon {
        margin-right: 4px;
      }
    }
    .arrow {
      padding: 4px 2px;
      border-left: 1px solid ${constants.COLORS.divider};
      border-radius: 0 4px 4px 0;
    }
    &.checkedIn,
    &.noShow {
      color: ${constants.COLORS.white};
      .arrow {
        border-left-color: rgba(255, 255, 255, 0.4);
      }
    }
    &.checkedIn {
      background-color: ${constants.COLORS.primary};
      .body:hover,
      .arrow:hover {
        background-color: ${constants.COLORS.secondary};
      }
    }
    &.noShow {
      background-color: ${constants.COLORS.danger};
      .body {
        cursor: default;
        padding: 4px 8px;
      }
      .body:hover,
      .arrow:hover {
        background-color: ${constants.COLORS.danger};
      }
      .arrow:hover {
        opacity: 0.8;
      }
    }
    .menu {
      position: absolute;
      top: calc(100% + 4px);
      right: 0;
      z-index: 10;
      min-width: 120px;
      margin: 0;
      padding: 4px 0;
      list-style: none;
      color: ${constants.COLORS.black};
      background-color: ${constants.COLORS.white};
      border: 1px solid ${constants.COLORS.divider};
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      li {
        padding: 6px 12px;
        white-space: nowrap;
        cursor: pointer;
        &:hover {
          background-color: ${constants.COLORS.background2};
        }
      }
    }
  }
  .tgToggle {
    float: right;
    margin-top: -20px;
    color: ${constants.COLORS.primary};
    font-size: 13px;
    transition: opacity 0.2s;
    cursor: pointer;
    &:hover {
      opacity: 0.75;
    }
    .icon {
      width: 20px;
      height: 20px;
      margin-right: 4px;
    }
    &.tgRemove {
      color: ${constants.COLORS.danger};
    }
    &.tgCancel {
      color: ${constants.COLORS.subText};
    }
  }
  .tgCanceled {
    float: right;
    margin-top: -20px;
    color: ${constants.COLORS.danger};
    font-size: 13px;
  }
  .createdAt {
    p {
      display: flex;
      color: ${constants.COLORS.black};
      font-size: 12px;
      span {
        flex: 0 0 auto;
        margin-right: 4px;
        color: ${constants.COLORS.subText};
        font-weight: 600;
        &::after {
          content: ':';
          margin: 0 4px;
        }
      }
    }
  }
  .details {
    p {
      display: flex;
      margin-bottom: 4px;
      color: ${constants.COLORS.black};
      font-size: 13px;
      &:last-child {
        margin-bottom: 0;
      }
      span {
        flex: 0 0 auto;
        margin-right: 4px;
        color: ${constants.COLORS.subText};
        font-weight: 600;
        &::after {
          content: ':';
          margin: 0 4px;
        }
      }
    }
  }
  .expandBtn {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    color: ${constants.COLORS.white};
    background-color: ${constants.COLORS.primary};
    border: 1px solid ${constants.COLORS.white};
    border-radius: 50%;
    transition: opacity 0.2s, transform 0.2s;
    opacity: 0;
    cursor: pointer;
    .icon {
      width: 20px;
      height: 20px;
    }
  }

  .otherRequestsContainer {
    font-size: 12px;

    summary.otherRequestsLabel {
      position: relative;
      display: block;
      font-size: 11px;
      color: ${constants.COLORS.subText};
      cursor: pointer;
      list-style: none;
      outline: none;

      &::-webkit-details-marker {
        display: none;
      }

      &::after {
        content: '';
        position: absolute;
        top: 4px;
        right: 4px;
        width: 8px;
        height: 8px;
        background-color: ${constants.COLORS.subText};
        clip-path: polygon(0 50%, 100% 0, 100% 100%);
        transform: rotate(0deg);
        transition: transform 0.2s, background-color 0.2s;
      }

      &:hover {
        &::after {
          background-color: ${constants.COLORS.primary};
        }
      }
    }

    &[open] summary.otherRequestsLabel::after {
      transform: rotate(-90deg);
    }

    .otherRequestsContent {
      margin-top: 4px;
      word-wrap: break-word;
    }
  }

  // セクション化に合わせて Pickup Location の背景・余白を外す
  .pickupLocation {
    padding: 0;
    margin-top: 0;
    background: transparent;
    &::before {
      font-size: 11px;
    }
  }

  @keyframes slideinRight2 {
    0% {
      transform: translateX(100%) translateY(-50%);
      opacity: 0;
    }
    100% {
      transform: translateX(0) translateY(-50%);
      opacity: 1;
    }
  }
`

export default Guest
